import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Globe } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { lang, setLang, t } = useLang();

  const links = [
    { to: "/", key: "nav.home" },
    { to: "/about", key: "nav.about" },
    { to: "/services", key: "nav.services" },
    { to: "https://happyhome.netmaid.com.sg/searchmaid", key: "nav.searchMaid", external: true },
    { to: "/application", key: "nav.application" },
    { to: "/blog", key: "nav.blog" },
    { to: "/resources", key: "nav.resources" },
    { to: "/contact", key: "nav.contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const LangToggle = () => (
    <button
      onClick={() => setLang(lang === "en" ? "zh" : "en")}
      data-testid="lang-toggle"
      className="flex items-center gap-1.5 text-xs tracking-wider uppercase border border-[#0A1128] px-3 py-2 hover:bg-[#0A1128] hover:text-white transition"
      aria-label="Toggle language"
    >
      <Globe className="w-3.5 h-3.5"/> {lang === "en" ? "中文" : "EN"}
    </button>
  );

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-gray-200" : "bg-white/80 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <img
            src="https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/cver0rlc_HappyHomeAgency%20Logo2026.png"
            alt="Happy Home Agency"
            className="h-12 w-auto"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif text-lg text-[#0A1128]">Happy Home Agency</div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-gray-500">License 06C4290 · Est. 2006</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            l.external ? (
              <a
                key={l.to}
                href={l.to}
                target="_blank"
                rel="noreferrer"
                data-testid={`nav-${l.key.split(".")[1].toLowerCase()}`}
                className="text-sm tracking-wide hh-underline pb-1 text-[#0A1128]/80 hover:text-[#0A1128]"
              >
                {t(l.key)}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.key.split(".")[1].toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide hh-underline pb-1 ${isActive ? "text-[#688A4E] font-semibold" : "text-[#0A1128]/80 hover:text-[#0A1128]"}`
                }
              >
                {t(l.key)}
              </NavLink>
            )
          ))}
          <LangToggle />
          <a
            href="https://wa.me/6591351039"
            target="_blank"
            rel="noreferrer"
            data-testid="navbar-cta"
            className="btn-primary text-sm"
          >
            <Phone className="w-4 h-4" /> 9135 1039
          </a>
        </nav>

        <div className="lg:hidden flex items-center gap-3">
          <LangToggle />
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="w-11 h-11 flex items-center justify-center border border-[#0A1128]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="navbar-mobile-menu" className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              l.external ? (
                <a key={l.to} href={l.to} target="_blank" rel="noreferrer" className="text-base text-[#0A1128]">{t(l.key)}</a>
              ) : (
                <NavLink key={l.to} to={l.to} className={({ isActive }) => `text-base ${isActive ? "text-[#688A4E] font-semibold" : "text-[#0A1128]"}`}>{t(l.key)}</NavLink>
              )
            ))}
            <a href="https://wa.me/6591351039" target="_blank" rel="noreferrer" className="btn-primary text-sm justify-center mt-2">
              <Phone className="w-4 h-4" /> 9135 1039
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
